import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { FullSegment, MyOrder, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { pnrToDocket } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { faFile, faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoPNRDocketProps {
  segment: FullSegment;
  orderRow: OrderRow;
  onSuccess: (row: MyOrder) => void;
}

const MoPNRDocket: FC<MoPNRDocketProps> = ({ segment, orderRow, ...props }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === segment.orderSegId ||
    activeOrder?.orderRow.segmentId === orderRow.segmentId;
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isChecked] = useState(true);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (openModal) {
      setStep(0);
    }
  }, [openModal]);

  const onClickPnrDocket = async () => {
    setIsLoading(true);
    setOpenModal(false);

    const res = await pnrToDocket({
      orderId: orderRow.orderId,
      segmentId: segment.orderSegId,
      productType: segment.productType,
    });

    if (res && !res?.hasErrors) {
      showSuccess(t('mo.docket_success'));
      props.onSuccess(res.myOrder);
      if (res.clipboardOrder) {
        try {
          await navigator.clipboard.writeText(res.clipboardOrder);
        } catch {
          showError('Failed to copy to clipboard #' + res.myOrder.orderRow.segmentId);
          console.error('Failed to copy to clipboard #' + res.myOrder.orderRow.segmentId);
        }
      }
    } else if (res?.hasErrors) {
      showError(res.errorMessage);
    }

    setIsLoading(false);
  };

  useOrdersAction(
    (action) =>
      action.action === 'pnr-to-docket' &&
      actionTargetsOrder(
        action,
        {
          segmentId: orderRow.segmentId,
          altSegmentId: segment.orderSegId,
          orderId: orderRow.orderId,
          altOrderId: segment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (action.confirm) {
        void onClickPnrDocket();
        return;
      }
      setOpenModal(true);
    },
    isActive,
  );

  return (
    <Fragment>
      <Button
        disabled={isLoading}
        onClick={() => setOpenModal(true)}
        title={t(isLoading ? 'mo.loading' : 'mo.pnr_to_docket')}
        icon={faFile}
        iconclassname="mr-5"
        className="btn btn-primary btn-ssm"
      />

      {openModal && createPortal(
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <h5 className="title">
                {t('mo.pnr_to_docket')}{' '}
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </h5>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
              </div>
            </div>

            {step === 0 && (
              <div className="modal-body">
                <div className="mb-10">{t('mo.segments')}</div>
                <Checkbox
                  checked={isChecked}
                  htmlLabel={
                    <span>
                      <span className="text-bold">[{segment.orderSegId}]</span>
                      <span className="ml-5 mo-status mo-status-secondary">
                        {t('mo.docket')}: {orderRow.docket}
                      </span>
                      {!!segment.suppPnrWithOutLock && (
                        <span className="ml-5 mo-status mo-status-secondary">
                          {t('mo.supp')}: {segment.suppPnrWithOutLock}
                        </span>
                      )}

                      <span className="ml-5">{segment.itemDesc}</span>
                    </span>
                  }
                />
              </div>
            )}

            {step === 1 && (
              <div className="modal-body">
                <div className="">{t('mo.are_you_sure')}</div>
              </div>
            )}

            <div className="modal-footer">
              {step === 0 && (
                <Button
                  className="btn btn-primary ml-0 mr-a"
                  title={t('save')}
                  onClick={() => setStep(1)}
                />
              )}
              {step === 1 && (
                <Button
                  className="btn btn-primary ml-0 mr-a"
                  title={t('yes')}
                  onClick={onClickPnrDocket}
                />
              )}
              <Button
                title={t('cancel')}
                className="btn btn-default"
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoPNRDocket, 'MoPNRDocket');
